import classNames from "classnames";
import Button from "kustom-front/src/components/atoms/Button";
import React, { FunctionComponent, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ReactComponent as ChevronLeft } from "@Icons/chevron-left.svg";
import { ReactComponent as ChevronRight } from "@Icons/chevron-right.svg";
import { MondialRelayLocationContent } from "@Types/addresses";
import { Day, OpeningHour } from "@Types/api/time";

import "./style.scss";

interface Props {
  locations: MondialRelayLocationContent[];
  onSelectLocation: (location: MondialRelayLocationContent) => void;
}

const CartMondialRelayLocations: FunctionComponent<Props> = ({ locations, onSelectLocation }) => {
  const intl = useIntl();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [scrollPercentage, setScrollPercentage] = useState<number>(0);

  const formatDistance = (distance: string) => (parseFloat(distance) / 1000).toFixed(1);
  const isClosedLocation = (day: OpeningHour[]) => day.length < 1;
  const formatDay = (day: Day) =>
    intl.formatMessage({ id: `cheerz_CartMondialRelayLocations_day_${day}` }).slice(0, 2);
  const numberStepsDisplayed = window.innerWidth < 520 ? 1 : window.innerWidth < 720 ? 2 : 3;
  const getMaxStep = () => Math.ceil(maxElements / numberStepsDisplayed);

  const maxElements: number = locations.length;
  const disabledRight: boolean = currentStep === getMaxStep();
  const disabledLeft: boolean = currentStep === 1;

  const scrollLeft = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setScrollPercentage(scrollPercentage + 100);
    }
  };

  const scrollRight = () => {
    if (currentStep < getMaxStep()) {
      setCurrentStep(currentStep + 1);
      setScrollPercentage(scrollPercentage - 100);
    }
  };

  const days: Day[] = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  return (
    <div className="CartMondialRelayLocations">
      <ChevronLeft
        onClick={scrollLeft}
        className={classNames("CartMondialRelayLocations-scrollLeft", {
          "CartMondialRelayLocations-scrollLeft--disabled": disabledLeft,
        })}
      />
      <ChevronRight
        onClick={scrollRight}
        className={classNames("CartMondialRelayLocations-scrollRight", {
          "CartMondialRelayLocations-scrollRight--disabled": disabledRight,
        })}
      />
      <div className="CartMondialRelayLocations-wrapper">
        <div
          className="CartMondialRelayLocations-scrollWrapper"
          style={{ transform: `translateX(${scrollPercentage}%)` }}
        >
          {locations.map((location) => (
            <div key={location.company} className="CartMondialRelayLocations-location">
              <div className="CartMondialRelayLocations-infos">
                <div className="CartMondialRelayLocations-company">{location.company}</div>
                <div className="CartMondialRelayLocations-address">{location.street}</div>
                <div className="CartMondialRelayLocations-adress2">{location.details}</div>
                <div className="CartMondialRelayLocations-city">
                  {location.postcode} {location.city}
                </div>
                <div className="CartMondialRelayLocations-distance">
                  <FormattedMessage
                    id={"cheerz_CartMondialRelayLocations_distance"}
                    values={{ distance: formatDistance(location.distance) }}
                  />
                </div>
              </div>
              <div className="CartMondialRelayLocations-openingHours">
                <div className="CartMondialRelayLocations-openingHoursTitle">
                  <FormattedMessage id={"cheerz_CartMondialRelayLocations_opening_hours"} />
                </div>
                {days.map((day) => {
                  return (
                    <div key={day} className="CartMondialRelayLocations-day">
                      <div className="CartMondialRelayLocations-dayName">{formatDay(day)}</div>
                      <div className="CartMondialRelayLocations-dayHours">
                        {isClosedLocation(location.openingHours[day]) ? (
                          <FormattedMessage id="cheerz_CartMondialRelayLocations_day_closed" />
                        ) : (
                          location.openingHours[day].map((hour: OpeningHour) => (
                            <div
                              key={`${day}${hour.from}`}
                              className="CartMondialRelayLocations-dayHour"
                            >
                              <span>{hour.from}</span> - <span>{hour.to}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="CartMondialRelayLocations-button">
                <Button onClick={() => onSelectLocation(location)} type="primary">
                  <FormattedMessage id="cheerz_CartMondialRelayLocations_select" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CartMondialRelayLocations;
